import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';
import { useI18n } from 'vue-i18n';
import { es, enUS, fr } from 'date-fns/locale';

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat);
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'MMM d, yyyy') => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const messageDate = format(messageTime, dateFormat);
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, 'LLL d y, h:mm a');
  }
  return messageDate;
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  const unixTime = fromUnixTime(time);
  return formatDistanceToNow(unixTime, { addSuffix: true });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, df);
};

export const dateFormatLocale = (time,locale  = 'es') => {
  const unixTime = fromUnixTime(time);
  const { t } = useI18n();

  const localesMap = {
    es: es,
    en: enUS,
    fr: fr,
  };

  const dateFormatString = '' + t('CONVERSATION.DATE_FORMAT_TOOLTIP') + '';
  const selectedLocale = localesMap[locale];
  return format(unixTime, dateFormatString, { locale: selectedLocale });


}

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time ,withAgo = false) => {
  const { t } = useI18n();
  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ` ${t('CONVERSATION.A_MINUTE_AGO')}` : '';

   const timeMappings = {
    'now'  :  t('CONVERSATION.NOW'),
    'less than a minute ago': t('CONVERSATION.NOW'),
    'in less than a minute': t('CONVERSATION.NOW'),
    'a minute ago':  t('AGENT_MGMT.MULTI_SELECTOR.LIST.NONE') ,
    'an hour ago': t('CONVERSATION.AN_HOUR_AGO'),
    'a day ago': t('CONVERSATION.A_DAY_AGO'),
    'a month ago': t('CONVERSATION.A_MONTH_AGO'),
    'a year ago': t('CONVERSATION.A_YEAR_AGO'),
  };

  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }

  const minute = t('CONVERSATION.MINUTE_SHORT');
  const hour = t('CONVERSATION.HOUR_SHORT');
  const day = t('CONVERSATION.DAY_SHORT');
  const month = t('CONVERSATION.MONTH_SHORT');
  const year = t('CONVERSATION.YEAR_SHORT');

  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `${minute}${suffix}`)
    .replace(' minutes ago', `${minute}${suffix}`)
    .replace(' hour ago', `${hour}${suffix}`)
    .replace(' hours ago', `${hour}${suffix}`)
    .replace(' day ago', `${day}${suffix}`)
    .replace(' days ago', `${day}${suffix}`)
    .replace(' month ago', `${month}${suffix}`)
    .replace(' months ago', `${month}${suffix}`)
    .replace(' year ago', `${year}${suffix}`)
    .replace(' years ago', `${year}${suffix}`);

    return convertToShortTime;
};

export const shortTimestampLocale = (t ,time ,withAgo = false) => {
  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ` ${t('CONVERSATION.A_MINUTE_AGO')}` : '';

   const timeMappings = {
    'now'  :  t('CONVERSATION.NOW'),
    'less than a minute ago': t('CONVERSATION.NOW'),
    'in less than a minute': t('CONVERSATION.NOW'),
    'a minute ago':  t('AGENT_MGMT.MULTI_SELECTOR.LIST.NONE') ,
    'an hour ago': t('CONVERSATION.AN_HOUR_AGO'),
    'a day ago': t('CONVERSATION.A_DAY_AGO'),
    'a month ago': t('CONVERSATION.A_MONTH_AGO'),
    'a year ago': t('CONVERSATION.A_YEAR_AGO'),
  };

  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }

  const minute = t('CONVERSATION.MINUTE_SHORT');
  const hour = t('CONVERSATION.HOUR_SHORT');
  const day = t('CONVERSATION.DAY_SHORT');
  const month = t('CONVERSATION.MONTH_SHORT');
  const year = t('CONVERSATION.YEAR_SHORT');

  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `${minute}${suffix}`)
    .replace(' minutes ago', `${minute}${suffix}`)
    .replace(' hour ago', `${hour}${suffix}`)
    .replace(' hours ago', `${hour}${suffix}`)
    .replace(' day ago', `${day}${suffix}`)
    .replace(' days ago', `${day}${suffix}`)
    .replace(' month ago', `${month}${suffix}`)
    .replace(' months ago', `${month}${suffix}`)
    .replace(' year ago', `${year}${suffix}`)
    .replace(' years ago', `${year}${suffix}`);

    return convertToShortTime;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
